import { call, put, takeLatest } from "redux-saga/effects";
import { IBaseResponse } from "../../../interfaces/base";
import {
  ICreateWorkoutAction,
  IDeleteWorkoutAction,
  IGetWorkoutAction,
  IUpdateWorkoutAction,
} from "../../../interfaces/store/workout-interface";
import {
  getWorkoutFailed,
  getWorkoutSuccess,
  getWorkoutStart,
  createWorkoutStart,
  createWorkoutSuccess,
  createWorkoutFailed,
  updateWorkoutStart,
  updateWorkoutSuccess,
  updateWorkoutFailed,
  deleteWorkoutStart,
  deleteWorkoutFailed,
  deleteWorkoutSuccess,
  createWorkoutInProgramStart,
  updateWorkoutInProgramStart,
  fetchTrainingProgramsForWorkoutStart,
  fetchTrainingProgramsForWorkoutFailed,
  fetchTrainingProgramsForWorkoutSuccess,
} from "./workout-slice";
import {
  deleteWorkoutApi,
  getWorkoutApi,
  updateWorkoutApi,
} from "../../../api/workouts";
import { createWorkoutApi } from "../../../api/workouts";
import { IUploadFileParams } from "../../../interfaces/files/files";
import { uploadImage } from "../../utils/fileUpload";
import {
  ICreateWorkoutInProgramAction,
  IUpdateWorkoutInProgramAction,
} from "../../../interfaces/store/training-program-interface";
import {
  addWorkoutToTrainingProgramApi,
  moveWorkoutInProgramApi,
  searchTrainingProgramsApi,
} from "../../../api/training-programs";
import {
  getTrainingProgramStart,
  updateTrainingProgramSuccess,
} from "../training-program/training-program-slice";
import { base64ToBlob } from "../../../utils/utils";
import { v4 as uuidv4 } from "uuid";
import { IFetchTrainingProgramsAction } from "../../../interfaces/store/training-programs-interface";

function* _getWorkout({ payload }: IGetWorkoutAction) {
  try {
    const { workoutId } = payload;

    if (!workoutId) {
      yield put(getWorkoutFailed({ message: "Workout ID is required" }));
      return;
    }

    const workout: IBaseResponse = yield call(getWorkoutApi, workoutId);

    if (workout.error) {
      yield put(getWorkoutFailed(workout.error));
    }

    yield put(getWorkoutSuccess(workout.data));
  } catch (error: any) {
    yield put(getWorkoutFailed({ message: error.message }));
  }
}

function* _createWorkout({ payload }: ICreateWorkoutAction) {
  try {
    const response: IBaseResponse = yield call(createWorkoutApi, payload);

    const { imageData, largeImageData } = payload;

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile),
        }
      : undefined;

    const blobLargeImageData = largeImageData
      ? {
          ...largeImageData,
          binaryFile: base64ToBlob(largeImageData.binaryFile),
        }
      : undefined;

    yield call(handleMediaData, blobImageData, response.data._id, "outerCover");
    yield call(
      handleMediaData,
      blobLargeImageData,
      response.data._id,
      "innerCover"
    );

    if (response.error) {
      yield put(createWorkoutFailed(response.error));
    } else {
      yield put(createWorkoutSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(createWorkoutFailed({ message: error.message }));
  }
}

function* _addWorkoutToProgram({ payload }: ICreateWorkoutInProgramAction) {
  try {
    const { workout, trainingProgramId, index, imageData, largeImageData } =
      payload;

    const response: IBaseResponse = yield call(addWorkoutToTrainingProgramApi, {
      trainingProgramId,
      index,
      workout,
    });

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile),
        }
      : undefined;

    const blobLargeImageData = largeImageData
      ? {
          ...largeImageData,
          binaryFile: base64ToBlob(largeImageData.binaryFile),
        }
      : undefined;

    yield call(handleMediaData, blobImageData, response.data._id, "outerCover");
    yield call(
      handleMediaData,
      blobLargeImageData,
      response.data._id,
      "innerCover"
    );

    if (response.error) {
      yield put(createWorkoutFailed(response.error));
    } else {
      yield put(createWorkoutSuccess(response.data));
      yield put(getTrainingProgramStart({ trainingProgramId }));
    }
  } catch (error: any) {
    let errorMessage = error.message;
    if (
      error.response.data.error === "INDEX ALREADY OCCUPIED IN TRAINING PROGRAM"
    ) {
      errorMessage = "Index already occupied in training program";
    }
    yield put(createWorkoutFailed({ message: errorMessage }));
  }
}

function* _updateWorkoutInProgram({ payload }: IUpdateWorkoutInProgramAction) {
  try {
    const {
      workout,
      trainingProgramId,
      imageData,
      largeImageData,
      index,
      workoutId,
    } = payload;

    yield call(_updateWorkout, {
      payload: { workoutId, workout, imageData, largeImageData },
      type: updateWorkoutStart.type,
    });

    if (index !== null && index !== undefined && index !== -1) {
      const response: IBaseResponse = yield call(moveWorkoutInProgramApi, {
        trainingProgramId,
        workoutId,
        index,
      });

      if (response.error) {
        yield put(updateWorkoutFailed(response.error));
      } else {
        yield put(updateTrainingProgramSuccess(response.data));
      }
    }
    yield put(getTrainingProgramStart({ trainingProgramId }));
  } catch (error: any) {
    let errorMessage = error.message;
    if (
      error.response.data.error === "INDEX ALREADY OCCUPIED IN TRAINING PROGRAM"
    ) {
      errorMessage = "Index already occupied in training program";
    }
    yield put(updateWorkoutFailed({ message: errorMessage }));
  }
}

function* _updateWorkout({ payload }: IUpdateWorkoutAction) {
  try {
    const { workoutId, workout, imageData, largeImageData } = payload;

    const blobImageData = imageData
      ? {
          ...imageData,
          binaryFile: base64ToBlob(imageData.binaryFile),
        }
      : undefined;

    const blobLargeImageData = largeImageData
      ? {
          ...largeImageData,
          binaryFile: base64ToBlob(largeImageData.binaryFile),
        }
      : undefined;

    const uuidOuter = uuidv4();
    const uuidInner = uuidv4();
    if (blobImageData && blobImageData.binaryFile) {
      yield call(uploadImage, blobImageData, workoutId, uuidOuter);
    }
    if (blobLargeImageData && blobLargeImageData.binaryFile) {
      yield call(uploadImage, blobLargeImageData, workoutId, uuidInner);
    }

    const updateBody = workout;

    if (updateBody && blobImageData) {
      if (blobImageData.fileName) {
        updateBody.outerCover =
          workoutId + "/" + uuidOuter + blobImageData.fileName;
      } else if (blobImageData.binaryFile === null) {
        updateBody.outerCover = null;
      }
    }
    if (updateBody && blobLargeImageData) {
      if (blobLargeImageData.fileName) {
        updateBody.innerCover =
          workoutId + "/" + uuidInner + blobLargeImageData.fileName;
      } else if (blobLargeImageData.binaryFile === null) {
        updateBody.innerCover = null;
      }
    }

    const response: IBaseResponse = yield call(updateWorkoutApi, {
      workoutId,
      workout,
    });

    if (response.error) {
      yield put(updateWorkoutFailed(response.error));
    } else {
      yield put(updateWorkoutSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(updateWorkoutFailed({ message: error.message }));
  }
}

function* _deleteWorkout({ payload }: IDeleteWorkoutAction) {
  try {
    const response: IBaseResponse = yield call(
      deleteWorkoutApi,
      payload.workoutId
    );

    if (response.error) {
      yield put(deleteWorkoutFailed(response.error));
    } else {
      yield put(deleteWorkoutSuccess(response.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(deleteWorkoutFailed({ message: error.message }));
  }
}

function* handleMediaData(
  mediaData: IUploadFileParams,
  responseId: string,
  fieldName: "image" | "video" | "outerCover" | "innerCover"
) {
  const uuid = uuidv4();
  if (mediaData && responseId) {
    if (mediaData.binaryFile) {
      yield call(uploadImage, mediaData, responseId, uuid);
    }
    yield call(_updateWorkout, {
      payload: {
        workout: {
          [fieldName]: mediaData.binaryFile
            ? responseId + "/" + uuid + mediaData.fileName
            : null,
        },
        workoutId: responseId,
      },
      type: updateWorkoutStart.type,
    });
  }
}

function* _fetchTrainingProgramsForWorkoutStart({
  payload,
}: IFetchTrainingProgramsAction) {
  try {
    const { sort, filter, pagination, search } = payload;

    const response: IBaseResponse = yield call(searchTrainingProgramsApi, {
      sort: sort || { sortBy: "updatedAt", sortOrder: -1 },
      search,
      filter,
      pagination,
    });

    if (response.error) {
      yield put(fetchTrainingProgramsForWorkoutFailed(response.error));
    } else {
      yield put(fetchTrainingProgramsForWorkoutSuccess(response.data.data));
    }
  } catch (error: any) {
    console.log("error:", error);
    yield put(
      fetchTrainingProgramsForWorkoutFailed({ message: error.message })
    );
  }
}

function* workoutSaga() {
  yield takeLatest(getWorkoutStart.type, _getWorkout);
  yield takeLatest(createWorkoutStart.type, _createWorkout);
  yield takeLatest(updateWorkoutStart.type, _updateWorkout);
  yield takeLatest(deleteWorkoutStart.type, _deleteWorkout);
  yield takeLatest(createWorkoutInProgramStart.type, _addWorkoutToProgram);
  yield takeLatest(updateWorkoutInProgramStart.type, _updateWorkoutInProgram);
  yield takeLatest(
    fetchTrainingProgramsForWorkoutStart.type,
    _fetchTrainingProgramsForWorkoutStart
  );
}

export default workoutSaga;
